import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useFormik } from 'formik';
import * as yup from 'yup';
import Page from "../../../components/Page";
import isEmbeddedBrowser from "../../../utils/embeddedBrowserChecker";
import { useState, useEffect } from 'react';
import Article from "../../../components/Article";
import { useOutletContext } from "react-router-dom";
import useInitiateSocialLogin from "../useInitiateSocialLogin";
import { useTenant } from "../../../contexts/TenantContext";
import CustomButton from "../../../components/CustomButton";
import ThemedTextField from "../../../components/ThemedTextField";
import AuthLink from "../components/AuthLink";
import PendingVerificationNotice from "../components/PendingVerificationNotice";
import useAuthRequest from "../useAuthRequest";
import useAuthNextStep from "../useAuthNextStep";
import { emailRule, passwordRule } from "../validation";
import { Routes } from "../../../routes/RouteConstants";

const validationSchema = yup.object({
  email: emailRule,
  password: passwordRule,
});

// docs/EMAIL_AUTH.md A1: email and password, then registration, then Google.
const LoginLayout = () => {
  const { initiateSocialLogin, loading } = useInitiateSocialLogin();
  const login = useAuthRequest("login");
  const goToNextStep = useAuthNextStep();
  const [embeddedBrowser, setEmbeddedBrowser] = useState(false);
  const { forceHeaderReRender } = useOutletContext();
  const { displayConfig } = useTenant();

  useEffect(() => {
    setEmbeddedBrowser(isEmbeddedBrowser());
    forceHeaderReRender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async ({ email, password }) => {
      const authResponse = await login.send({ email, password });
      // A correct password on a registration never confirmed answers with a new code.
      goToNextStep(authResponse, { email: email.trim(), purpose: "REGISTRATION" });
    },
  });

  const handleSocialLogin = async () => {
    const redirectUrl = await initiateSocialLogin();
    if (redirectUrl) {
      window.location = redirectUrl;
    }
  };

  return (
    <Page>
      <Article xsHorizontalPadding={2} title="Bejelentkezés">
        <PendingVerificationNotice />
        <form onSubmit={formik.handleSubmit} style={{ width: '100%' }} noValidate>
          <Stack spacing={3}>
            <ThemedTextField
              id="email"
              name="email"
              label="Email cím"
              type="email"
              autoComplete="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <ThemedTextField
              id="password"
              name="password"
              label="Jelszó"
              type="password"
              autoComplete="current-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
            <CustomButton text="Bejelentkezés" type="submit" isLoading={login.loading} />
          </Stack>
        </form>

        <Stack spacing={1} alignItems="center" mt={3}>
          <AuthLink to={Routes.FORGOT_PASSWORD}>Elfelejtett jelszó?</AuthLink>
          <Typography variant="body2" color={displayConfig?.color}>
            Még nincs fiókod? <AuthLink to={Routes.REGISTER}>Regisztráció</AuthLink>
          </Typography>
        </Stack>

        <Divider sx={{ width: '100%', my: 3, color: displayConfig?.color }}>vagy</Divider>

        {/* While loading, the icon and the label keep their place, invisible, and a spinner sits over
            them, as on CustomButton: the button keeps its size and its outline. */}
        {!embeddedBrowser &&
          <Button
            variant="outlined"
            color="primary"
            disabled={loading}
            aria-busy={loading}
            aria-label={loading ? "Bejelentkezés Google-lal" : undefined}
            sx={{ '&.Mui-disabled': { borderColor: (theme) => alpha(theme.palette.primary.main, 0.5) } }}
            startIcon={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "26px",
                  height: "26px",
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  visibility: loading ? "hidden" : "visible",
                }}
              >
                <img src="/google-icon.svg" alt="Google Icon" style={{ width: '16px', height: '16px', display: 'block' }} />
              </Box>
            }
            onClick={() => handleSocialLogin()}
          >
            <Typography textTransform="none" color={displayConfig?.color} noWrap visibility={loading ? "hidden" : "visible"}>
              Bejelentkezés Google-lal
            </Typography>
            {loading && (
              <CircularProgress
                size={20}
                sx={{ position: 'absolute', top: 'calc(50% - 10px)', left: 'calc(50% - 10px)', color: displayConfig?.color }}
              />
            )}
          </Button>
        }

        {/* Google refuses to sign in inside an in-app browser; the form above works there. */}
        {embeddedBrowser && (
          <Typography variant="body2" color="error" textAlign="center" fontWeight="bold">
            Az alkalmazásokba épített böngészőkben (pl. Messenger, Instagram, TikTok) a Google-bejelentkezés nem működik.
            Ha Google-lel szeretnél belépni, nyisd meg az oldalt egy másik böngészőben (pl. Chrome vagy Safari).
            Email címmel itt is be tudsz lépni.
          </Typography>
        )}
      </Article>
    </Page>
  );
};

export default LoginLayout;
